import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpCircle, History, RotateCcw } from "lucide-react";
import * as React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Skeleton,
  Textarea,
} from "@/components/ui/primitives";
import { toast } from "@/components/ui/toaster";
import { diffVersion, getLive, promoteVersion, rollbackLive } from "@/lib/api/endpoints";
import { useMe } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import { formatDate } from "@/lib/utils";
import type { LiveVersion, SetChange, VersionDiff } from "@/types/api";

// "Live" (AVI-168) is what callers get: the `latest` tag that /api/v1 resolves
// and the published version the chat app uses. Promote and rollback move both.

const useIsOrgAdmin = () => useMe().data?.role === "tenant_admin";

async function refreshAfterSwitch(qc: ReturnType<typeof useQueryClient>, slug: string) {
  await qc.invalidateQueries({ queryKey: qk.live(slug) });
  await qc.invalidateQueries({ queryKey: qk.versions(slug) });
  await qc.invalidateQueries({ queryKey: ["my", "agents", slug, "versions"] });
}

/** A note dialog shared by promote and rollback. */
function NoteDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  pending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  pending: boolean;
  onConfirm: (note: string | null) => void;
}) {
  const [note, setNote] = React.useState("");
  React.useEffect(() => {
    if (open) setNote("");
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="live-note">Note (optional)</Label>
          <Textarea
            id="live-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Why — kept in the version history."
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={pending} onClick={() => onConfirm(note.trim() || null)}>
            {pending ? "Working…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Agent page: which version is live, one-click rollback, and the history. */
export function LiveVersionCard({ agentSlug }: { agentSlug: string }) {
  const qc = useQueryClient();
  const isAdmin = useIsOrgAdmin();
  const live = useQuery({ queryKey: qk.live(agentSlug), queryFn: () => getLive(agentSlug) });
  const [open, setOpen] = React.useState(false);

  const rollback = useMutation({
    mutationFn: (note: string | null) => rollbackLive(agentSlug, note),
    onSuccess: async (r: LiveVersion) => {
      toast.success(`Rolled back — ${r.live} is live.`);
      setOpen(false);
      await refreshAfterSwitch(qc, agentSlug);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (live.isLoading) return <Skeleton className="mb-6 h-24 w-full" />;
  const data = live.data;
  if (!data) return null;

  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          Live version
          {data.live ? (
            <Badge variant="success">{data.live}</Badge>
          ) : (
            <span className="text-sm font-normal text-muted-foreground">none yet</span>
          )}
        </CardTitle>
        {isAdmin && data.rollback_to ? (
          <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
            <RotateCcw className="h-4 w-4" />
            Roll back to {data.rollback_to}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          What callers get: the <code>latest</code> tag on the API and the published version in the
          chat app. Promote a version from its page; only org admins can promote or roll back.
        </p>
        {data.history.length > 0 ? (
          <ul className="space-y-1.5 text-sm">
            {data.history.slice(0, 5).map((h, i) => (
              <li key={i} className="flex flex-wrap items-baseline gap-x-2">
                <History className="h-3.5 w-3.5 self-center text-muted-foreground" />
                <span className="font-medium">
                  {h.action === "rollback" ? "Rolled back" : "Promoted"}
                </span>
                <span className="font-mono text-xs">
                  {h.from_version ?? "—"} → {h.to_version ?? "(deleted)"}
                </span>
                <span className="text-xs text-muted-foreground">
                  {h.actor} · {formatDate(h.at)}
                </span>
                {h.note ? <span className="w-full pl-5 text-xs">“{h.note}”</span> : null}
              </li>
            ))}
          </ul>
        ) : null}
      </CardContent>
      <NoteDialog
        open={open}
        onOpenChange={setOpen}
        title={`Roll back to ${data.rollback_to}?`}
        description={`${data.rollback_to} becomes what callers get again: the latest tag and the published version. ${data.live ?? "The current version"} stays as it is and can be promoted again.`}
        confirmLabel="Roll back"
        pending={rollback.isPending}
        onConfirm={(note) => rollback.mutate(note)}
      />
    </Card>
  );
}

function fmt(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

function SetChangeRow({ label, change }: { label: string; change: SetChange }) {
  if (!change.added.length && !change.removed.length && !change.changed.length) return null;
  return (
    <div className="text-sm">
      <span className="font-medium">{label}: </span>
      {change.added.length ? (
        <span className="text-success">+ {change.added.join(", ")} </span>
      ) : null}
      {change.removed.length ? (
        <span className="text-destructive">− {change.removed.join(", ")} </span>
      ) : null}
      {change.changed.length ? <span>changed: {change.changed.join(", ")}</span> : null}
    </div>
  );
}

function isIdentical(d: VersionDiff): boolean {
  return (
    d.fields.length === 0 &&
    [d.skills, d.tools, d.knowledge_bases, d.validators].every(
      (s) => !s.added.length && !s.removed.length && !s.changed.length,
    )
  );
}

/** Version page: what changes if this version goes live, and the Promote button. */
export function ComparePromoteCard({
  agentSlug,
  label,
  status,
}: {
  agentSlug: string;
  label: string;
  /** The version's status; a retired version can't be promoted. */
  status: string;
}) {
  const qc = useQueryClient();
  const isAdmin = useIsOrgAdmin();
  const diff = useQuery({
    queryKey: qk.versionDiff(agentSlug, label),
    queryFn: () => diffVersion(agentSlug, label),
  });
  const [open, setOpen] = React.useState(false);

  const promote = useMutation({
    mutationFn: (note: string | null) => promoteVersion(agentSlug, label, note),
    onSuccess: async (r: LiveVersion) => {
      toast.success(`${r.live} is now live.`);
      setOpen(false);
      await refreshAfterSwitch(qc, agentSlug);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (diff.isLoading) return <Skeleton className="h-32 w-full" />;
  const d = diff.data;
  if (!d) return null;
  const isLive = d.base === d.target;
  const retired = status === "retired";

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">
          {isLive ? "This is the live version" : `Compare with live (${d.base ?? "none"})`}
        </CardTitle>
        {isAdmin && !isLive && retired ? (
          <span className="text-xs text-muted-foreground">
            Retired — set the status to draft or active to promote it.
          </span>
        ) : isAdmin && !isLive ? (
          <Button size="sm" onClick={() => setOpen(true)}>
            <ArrowUpCircle className="h-4 w-4" />
            Promote to live
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {d.target_change_note ? (
          <p className="rounded-md border border-border p-2 text-sm">
            <span className="font-medium">Change note: </span>
            {d.target_change_note}
          </p>
        ) : null}
        {isLive ? null : isIdentical(d) ? (
          <p className="text-sm text-muted-foreground">Same configuration as the live version.</p>
        ) : (
          <>
            {d.fields.length ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="w-1/4 py-1">Setting</th>
                    <th className="w-[37.5%] py-1">Live ({d.base ?? "none"})</th>
                    <th className="w-[37.5%] py-1">This version</th>
                  </tr>
                </thead>
                <tbody>
                  {d.fields.map((f) => (
                    <tr key={f.field} className="border-t border-border align-top">
                      <td className="py-1.5 font-mono text-xs">{f.field}</td>
                      <td className="whitespace-pre-wrap break-words py-1.5 text-xs text-muted-foreground">
                        {fmt(f.base)}
                      </td>
                      <td className="whitespace-pre-wrap break-words py-1.5 text-xs">
                        {fmt(f.target)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
            <SetChangeRow label="Skills" change={d.skills} />
            <SetChangeRow label="Tools" change={d.tools} />
            <SetChangeRow label="Knowledge bases" change={d.knowledge_bases} />
            <SetChangeRow label="Validators" change={d.validators} />
          </>
        )}
      </CardContent>
      <NoteDialog
        open={open}
        onOpenChange={setOpen}
        title={`Promote ${label} to live?`}
        description={`${label} becomes what callers get: the latest tag on the API and the published version in the chat app.${d.base ? ` You can roll back to ${d.base} in one click.` : ""}`}
        confirmLabel="Promote"
        pending={promote.isPending}
        onConfirm={(note) => promote.mutate(note)}
      />
    </Card>
  );
}
